import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { getUsageStats, UsageStatsResponse } from '../services/Trial/trialApi';

interface UsageStatsContextType {
  usageStats: Record<string, UsageStatsResponse | null>;
  loading: Record<string, boolean>;
  fetchUsageStats: (organizationId: string) => Promise<UsageStatsResponse | null>;
}

const UsageStatsContext = createContext<UsageStatsContextType | undefined>(undefined);

export const UsageStatsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [usageStats, setUsageStats] = useState<Record<string, UsageStatsResponse | null>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  // Track in-flight requests to prevent duplicate calls
  const pendingRequests = useRef<Record<string, Promise<UsageStatsResponse | null> | undefined>>({});

  const fetchUsageStats = useCallback(async (organizationId: string): Promise<UsageStatsResponse | null> => {
    // Return cached data if available
    if (organizationId in usageStats) {
      return usageStats[organizationId];
    }

    // If a request is already in-flight for this org, wait for it
    const pendingRequest = pendingRequests.current[organizationId];
    if (pendingRequest) {
      return pendingRequest;
    }

    // Start new request
    setLoading(prev => ({ ...prev, [organizationId]: true }));

    const requestPromise = (async () => {
      try {
        const stats = await getUsageStats(organizationId);
        setUsageStats(prev => ({ ...prev, [organizationId]: stats }));
        return stats;
      } catch (error) {
        console.error('Failed to fetch usage stats:', error);
        setUsageStats(prev => ({ ...prev, [organizationId]: null }));
        return null;
      } finally {
        setLoading(prev => ({ ...prev, [organizationId]: false }));
        pendingRequests.current[organizationId] = undefined;
      }
    })();

    pendingRequests.current[organizationId] = requestPromise;
    return requestPromise;
  }, [usageStats]);

  return (
    <UsageStatsContext.Provider value={{ usageStats, loading, fetchUsageStats }}>
      {children}
    </UsageStatsContext.Provider>
  );
};

export const useUsageStats = () => {
  const context = useContext(UsageStatsContext);
  if (context === undefined) {
    throw new Error('useUsageStats must be used within a UsageStatsProvider');
  }
  return context;
};
